import React, {useRef} from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import UniversalBlockGlobalClientStates from 'insights/universal-blocks/UniversalBlockGlobalClientStates';
import {RunQueryState} from 'insights/warehouse/AnalyticsWarehouseTypes';
import {convertRunQueryStateToLegacyState} from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import {useWritableContext} from 'insights/WritableContext';
import Menu from 'ui-library/menus/Menu';
import Section from 'ui-library/menus/Section';

import BaseBlockOverflowDropdown from './BaseBlockOverflowDropdown';
import {SHARED_MENU_PROPS} from './BlockOverflowMenuConstants';
import getAddToWorklistOption from './getAddToWorklistOption';
import getCloneBlockOption from './getCloneBlockOption';
import getCopyAsTSVOption from './getCopyAsTSVOption';
import getDeleteBlockOption from './getDeleteBlockOption';
import getDownloadAsCSVOption, {ExportBlockFn} from './getDownloadAsCSVOption';
import getExecuteRequestOption from './getExecuteRequestOption';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  cloneBlock: () => void;
  deleteBlock: () => void;
  exportBlock: ExportBlockFn;
}

interface Props {
  actions: Actions;
  blockViewStateId: string;
  className?: string;
  dashboardAttrs: Pick<AnalyticsDashboard, 'analyticsQueries'>;
  runQueryState: RunQueryState;
}

/**
 * Overflow menu for dashboard editor's block
 *
 * Options to download CSV, copy TSV, add to worklist, duplicate block, and delete block
 */
export default function AnalyticsBlockOverflowDropdown({
  actions,
  blockViewStateId,
  className,
  dashboardAttrs,
  runQueryState,
}: Props): JSX.Element {
  const [sortState] = UniversalBlockGlobalClientStates.sortState.useGlobalClientState(blockViewStateId);
  const writable = useWritableContext();
  const tsvTextRef = useRef<string | null>('');
  const optionProps = {
    actions,
    runQueryState: convertRunQueryStateToLegacyState(runQueryState),
    showRawOutput: false,
    sortState,
    tsvTextRef,
  };
  return (
    <BaseBlockOverflowDropdown
      className={className}
      menu={
        <Menu {...SHARED_MENU_PROPS}>
          <Section>
            {getDownloadAsCSVOption(optionProps)}
            {getCopyAsTSVOption(optionProps)}
            {getAddToWorklistOption(optionProps)}
            {getExecuteRequestOption(optionProps)}
          </Section>
          {writable && (
            <Section>
              {getCloneBlockOption({...optionProps, dashboardAttrs})}
              {getDeleteBlockOption(optionProps)}
            </Section>
          )}
        </Menu>
      }
    />
  );
}
